import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';
import { buildSearchParamsAuto } from '../utils';
import { PROVIDERS_TAG_TYPE } from './constants';

interface SystemVolumeCodeSetRow {
  code_set: { id: number; label: string; type?: string; scope?: string };
  claims: number;
  patients: number;
}

interface SystemVolumeEncounterRow {
  encounter: { id: number; label: string };
  claims: number;
  patients: number;
  encounters: number;
}

export interface SystemVolumeResponse {
  system: { id: string; name: string };
  code_sets: SystemVolumeCodeSetRow[];
  encounters: SystemVolumeEncounterRow[];
}

interface SystemVolumeQueryParams {
  systemId: string;
  codeSetIds?: number[];
  encounterIds?: number[];
  minYear?: number;
  maxYear?: number;
  filterSetId?: number;
}

interface CodeVolumeItem {
  code_name: string;
  claims: number;
  patients: number;
}

interface TreatingVolumeTrendsResponse {
  claims: number;
  patients: number;
  claims_trends: Record<string, number>;
  patients_trends: Record<string, number>;
}

interface CodeSetVolumeByCodeResponse {
  results: CodeVolumeItem[];
  next?: string | null;
  count?: number;
}

export const providersApi = createApi({
  reducerPath: 'providersApi',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [PROVIDERS_TAG_TYPE],
  endpoints: (builder) => ({
    getProviderById: builder.query<Record<string, any>, string | { providerId: string }>({
      query: (arg) => {
        const providerId = typeof arg === 'string' ? arg : arg.providerId;
        return `v1/entities/entity/${providerId}/`;
      },
      providesTags: (result, error, arg) => {
        const providerId = typeof arg === 'string' ? arg : arg.providerId;
        return [{ type: PROVIDERS_TAG_TYPE, providerId }];
      },
    }),
    getSystemSites: builder.query<MedScout.PaginatedResponse & { results: any[] }, Record<string, any>>({
      query: ({
        systemId,
        page,
        pageSize,
        sort,
        order,
        sort_id,
        sort_field,
        q,
        filterSets,
        data,
      }) => {
        const params = buildSearchParamsAuto(
          {
            page,
            pageSize,
            sort,
            order,
            sort_id,
            sort_field,
            q,
            filterSets,
          },
          {
            filterSets: 'filter_set',
          }
        );

        return {
          url: `v1/entities/system/${systemId}/sites/?${params.toString()}`,
          method: 'POST',
          body: data,
        };
      },
    }),
    getSystemSitesPaginated: builder.infiniteQuery<
      MedScout.PaginatedResponse & { results: any[] },
      Record<string, any>,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (!lastPage.next) return undefined;
          return lastPageParam + 1;
        },
      },
      query({ queryArg, pageParam }) {
        const params = buildSearchParamsAuto(
          {
            page: pageParam,
            pageSize: queryArg.pageSize,
            sort: queryArg.sort,
            order: queryArg.order,
            sort_id: queryArg.sort_id,
            sort_field: queryArg.sort_field,
            q: queryArg.q,
            filterSets: queryArg.filterSets,
          },
          {
            filterSets: 'filter_set',
          }
        );

        return {
          url: `v1/entities/system/${queryArg.systemId}/sites/?${params.toString()}`,
          method: 'POST',
          body: queryArg.data,
        };
      },
    }),
    getSystemSitesBySpecialty: builder.query<MedScout.PaginatedResponse & { results: { specialty: string; count: number }[] }, { systemId: string; sort?: string; order?: string }>({
      query: ({ systemId, sort, order }) => {
        return {
          url: `v1/entities/system/${systemId}/sites-by-specialty/`,
          params: {
            sort: sort,
            order: order,
          },
        };
      },
    }),
    getSystemSitesBySiteOfCareType: builder.query<MedScout.PaginatedResponse & { results: { site_of_care_type: string; count: number }[] }, { systemId: string; sort?: string; order?: string }>({
      query: ({ systemId, sort, order }) => {
        return {
          url: `v1/entities/system/${systemId}/sites-by-site-of-care-type/`,
          params: {
            sort: sort,
            order: order,
          },
        };
      },
    }),
    getProviderVolume: builder.query<Record<string, any>, { providerId: string; filter_set?: number; cohort_id?: number; dateRange?: any }>({
      query: ({ providerId, filter_set, cohort_id, dateRange }) => {
        const params = buildSearchParamsAuto({
          filter_set,
          cohort_id,
          dateRange,
        });
        return {
          url: `v1/entities/entity/${providerId}/volume/`,
          params,
        };
      },
      providesTags: (result, error, { providerId }) => [
        { type: PROVIDERS_TAG_TYPE, providerId },
      ],
    }),
    exportSystemSites: builder.mutation<void, Record<string, any>>({
      query: ({
        systemId,
        page,
        pageSize,
        sort,
        order,
        sort_id,
        sort_field,
        q,
        filterSets,
        data,
        fileFormat,
        columns,
        fileName,
      }) => {
        const params = buildSearchParamsAuto(
          {
            page,
            pageSize,
            sort,
            order,
            sort_id,
            sort_field,
            q,
            filterSets,
          },
          {
            filterSets: 'filter_set',
          }
        );

        const url = `https://api.medscout.io/api/v1/entities/system/${systemId}/sites/?${params.toString()}`;

        return {
          url: 'v1/admin/export/',
          method: 'POST',
          body: {
            method: 'POST',
            url,
            file_format: fileFormat,
            file_name: fileName,
            columns: columns,
            body: data,
          },
        };
      },
    }),
    getEntityDataCategories: builder.query<Record<string, boolean>, { providerId: string }>({
      query: ({ providerId }) =>
        `v1/entities/entity/${providerId}/available-data-categories/`,
      providesTags: (result, error, { providerId }) => [
        { type: PROVIDERS_TAG_TYPE, providerId },
      ],
    }),
    getEntityDataByCategories: builder.query<Record<string, any>, { providerId: string; company?: string }>({
      query: ({ providerId, company }) => {
        const params = buildSearchParamsAuto(company ? { company } : {});
        return `v1/entities/entity/${providerId}/open-payments-categories/?${params.toString()}`;
      },
      providesTags: (result, error, { providerId }) => [
        { type: PROVIDERS_TAG_TYPE, providerId },
      ],
    }),
    getPayerMix: builder.query<{ count: number; results: any[] }, { id: string }>({
      query: ({ id }) => ({
        url: `v1/providers/${id}/payer-mix/`,
        method: 'GET',
      }),
      providesTags: (result, error, { id }) => [
        { type: PROVIDERS_TAG_TYPE, id },
      ],
    }),
    getRelationships: builder.query<MedScout.PaginatedResponse & { results: any[] }, Record<string, any>>({
      query: ({
        provider_id,
        relationship_type,
        page,
        pageSize,
        sort,
        order,
        sort_search,
        sort_value,
        q,
        saved_search,
        mode,
      }) => {
        const params = buildSearchParamsAuto({
          relationship_type,
          page,
          pageSize,
          sort,
          order,
          sort_search,
          sort_value,
          q,
          saved_search,
          mode,
        });
        return {
          url: `v1/entities/entity/${provider_id}/relationships?${params}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, { provider_id }) => [
        { type: PROVIDERS_TAG_TYPE, provider_id },
      ],
    }),
    getCodeSetsVolume: builder.query<Record<string, any>, { entityId: string; params: Record<string, any> }>({
      query: ({ entityId, params }) => {
        const queryParams = buildSearchParamsAuto(params);
        const queryString = queryParams.toString();
        return {
          url: `v1/entities/entity/${entityId}/code-sets-volume/?${queryString}`,
        };
      },
    }),
    getPatientVisitValue: builder.query<
      { patient_visit_value: number },
      { entityId: string | number; codeSetId: number }
    >({
      query: ({ entityId, codeSetId }) => ({
        url: `v1/entities/entity/${entityId}/patient-visit-value/${codeSetId}/`,
      }),
    }),
    getCodeSetVolumeByCode: builder.query<
      CodeSetVolumeByCodeResponse,
      {
        entityId: string;
        codeSetId: number;
        minYear?: number;
        maxYear?: number;
      }
    >({
      query: ({ entityId, codeSetId, minYear, maxYear }) => {
        const params = buildSearchParamsAuto({
          ...(minYear != null && { min_year: minYear }),
          ...(maxYear != null && { max_year: maxYear }),
        });
        return {
          url: `v1/entities/entity/${entityId}/code-set-volume/${codeSetId}/by-code/?${params}`,
        };
      },
    }),
    getCodeSetVolumeByCodePaginated: builder.infiniteQuery<
      CodeSetVolumeByCodeResponse,
      { entityId: string; codeSetId: number; minYear?: number; maxYear?: number },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (!lastPage.next) return undefined;
          return lastPageParam + 1;
        },
      },
      query({ queryArg, pageParam }) {
        const params = buildSearchParamsAuto({
          page: pageParam,
          page_size: 25,
          ...(queryArg.minYear != null && { min_year: queryArg.minYear }),
          ...(queryArg.maxYear != null && { max_year: queryArg.maxYear }),
        });
        return {
          url: `v1/entities/entity/${queryArg.entityId}/code-set-volume/${queryArg.codeSetId}/by-code/?${params}`,
        };
      },
    }),
    getTreatingVolumeTrends: builder.query<
      TreatingVolumeTrendsResponse,
      {
        entityId: string;
        codeSetId: number;
        dateRange?: { gte: string; lte: string };
      }
    >({
      query: ({ entityId, codeSetId, dateRange }) => {
        const params = buildSearchParamsAuto({ dateRange });
        return {
          url: `v1/entities/entity/${entityId}/treating-volume-trends/${codeSetId}/?${params.toString()}`,
        };
      },
    }),
    getReferralVolumeTrends: builder.query<
      TreatingVolumeTrendsResponse,
      {
        entityId: string;
        codeSetId: number;
        direction: 'inbound' | 'outbound';
        dateRange?: { gte: string; lte: string };
      }
    >({
      query: ({ entityId, codeSetId, direction, dateRange }) => {
        const params = buildSearchParamsAuto({ direction, dateRange });
        return {
          url: `v1/entities/entity/${entityId}/referral-volume-trends/${codeSetId}/?${params.toString()}`,
        };
      },
    }),
    getSystemVolume: builder.query<SystemVolumeResponse, SystemVolumeQueryParams>({
      query: ({ systemId, codeSetIds, encounterIds, minYear, maxYear, filterSetId }) => {
        const params = buildSearchParamsAuto({
          ...(codeSetIds?.length && { code_set_ids: codeSetIds }),
          ...(encounterIds?.length && { encounter_ids: encounterIds }),
          ...(minYear != null && { min_year: minYear }),
          ...(maxYear != null && { max_year: maxYear }),
          ...(filterSetId != null && { filter_set: filterSetId }),
        });
        return {
          url: `v1/entities/entity/${systemId}/system-volume/?${params}`,
        };
      },
    }),
  }),
});

export const {
  useGetProviderByIdQuery,
  useGetSystemSitesQuery,
  useGetSystemSitesBySpecialtyQuery,
  useGetSystemSitesBySiteOfCareTypeQuery,
  useGetProviderVolumeQuery,
  useExportSystemSitesMutation,
  useGetEntityDataCategoriesQuery,
  useGetPayerMixQuery,
  useGetRelationshipsQuery,
  useGetCodeSetsVolumeQuery,
  useGetPatientVisitValueQuery,
  useGetCodeSetVolumeByCodeQuery,
  useGetCodeSetVolumeByCodePaginatedInfiniteQuery,
  useGetEntityDataByCategoriesQuery,
  useGetTreatingVolumeTrendsQuery,
  useGetReferralVolumeTrendsQuery,
  useGetSystemVolumeQuery,
  useGetSystemSitesPaginatedInfiniteQuery,
} = providersApi;
